/* eslint-disable react/prop-types */
import React, { useEffect, useRef } from 'react';
import { Avatar, Empty, Spin, Tag, Tooltip } from 'antd';
import moment from 'moment';

import { CheckCircleOutlined, CheckCircleTwoTone, UserOutlined } from '@ant-design/icons';

import Utils from '../../../../Assets/Scripts/Utils';
import { api } from '../../../../Services/axiosService';

import MessageBubble from './MessageBubble';

import 'moment/locale/pt-br';

function MessageList({ messages, loading, currentUserId, activeType, task }) {
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const userData = JSON.parse(localStorage.getItem('conecta__userData') || '{}');
  const isTransportCompanyUser = userData.isTransportCompany === true;

  moment.locale('pt-br');

  useEffect(() => {
    if (messagesEndRef.current && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    const markMessagesAsRead = async () => {
      if (!messages || messages.length === 0 || !task?.id || !currentUserId) {
        return;
      }

      const messagesToMark = messages.filter((msg) => {
        if (msg.userId === currentUserId || msg.isRead) {
          return false;
        }

        const isLogisticsUser = userData.role.id === 39;
        const isOperatorUser = task.scheduleType;
        const isLogisticsMessage = msg.to === 'Logistic';
        const isInstructorMessage = msg.to === 'Instructor';
        const isTransportMessage = msg.to === 'Transport';

        if (isLogisticsUser && isLogisticsMessage) {
          return true;
        }

        if (isInstructorMessage && isOperatorUser) {
          return task?.mainInstructor?.userId && currentUserId === task.mainInstructor.userId;
        }

        if (isTransportMessage && isTransportCompanyUser) {
          const userTransportCompanies =
            userData.transportCompanyList?.split(',').map((id) => parseInt(id.trim(), 10)) || [];
          return (
            task?.transportCompany?.id && userTransportCompanies.includes(task.transportCompany.id)
          );
        }

        return false;
      });

      if (messagesToMark.length > 0) {
        try {
          await Promise.all(messagesToMark.map((msg) => api.put(`/Communication/${msg.id}/read`)));
        } catch (error) {
          Utils.logError(error, 'Erro ao marcar mensagens como lidas');
        }
      }
    };

    markMessagesAsRead();
  }, [
    messages,
    currentUserId,
    activeType,
    task,
    isTransportCompanyUser,
    userData.transportCompanyList,
  ]);

  if (loading) {
    return (
      <div className="message-list-loading">
        <Spin tip="Carregando mensagens..." />
      </div>
    );
  }

  if (!messages || messages.length === 0) {
    return (
      <div className="message-list-empty">
        <Empty description="Não há mensagens ainda" />
      </div>
    );
  }

  const sortedMessages = [...messages].sort(
    (a, b) => new Date(a.createDate) - new Date(b.createDate)
  );

  const messagesByDate = {};
  sortedMessages.forEach((msg) => {
    const msgDate = moment(msg.createDate).startOf('day');
    const dateKey = msgDate.format('YYYY-MM-DD');
    if (!messagesByDate[dateKey]) {
      messagesByDate[dateKey] = { date: msgDate, messages: [] };
    }
    messagesByDate[dateKey].messages.push(msg);
  });

  const getDateLabel = (dateObj) => {
    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'day').startOf('day');
    if (dateObj.isSame(today)) return 'Hoje';
    if (dateObj.isSame(yesterday)) return 'Ontem';
    return dateObj.format('DD/MM/YYYY');
  };

  return (
    <div className="message-list" ref={containerRef}>
      {Object.keys(messagesByDate).map((dateKey) => (
        <React.Fragment key={dateKey}>
          <div className="date-separator">
            <Tag className="date-tag">{getDateLabel(messagesByDate[dateKey].date)}</Tag>
          </div>
          {messagesByDate[dateKey].messages.map((msg) => (
            <MessageBubble
              key={msg.id || `msg-${msg.createDate || Date.now()}`}
              placement={msg.userId === currentUserId ? 'end' : 'start'}
              content={msg.message}
              avatar={<Avatar icon={<UserOutlined />} />}
              header={
                <div className="message-header">
                  <span className="message-author">{msg.userName}</span>
                  {msg.userRole && <span className="message-role">{msg.userRole}</span>}
                </div>
              }
              footer={
                <div className="message-footer">
                  <span className="message-time">{moment(msg.createDate).format('HH:mm')}</span>
                  {msg.userId === currentUserId && (
                    <Tooltip
                      title={
                        msg.isRead && msg.readAt
                          ? `Lida em ${moment(msg.readAt).format('DD/MM/YYYY HH:mm')}`
                          : 'Não lida'
                      }
                      placement="bottom"
                    >
                      <span className="message-status" style={{ marginLeft: '8px' }}>
                        {msg.isRead ? (
                          <CheckCircleTwoTone twoToneColor="#52c41a" />
                        ) : (
                          <CheckCircleOutlined />
                        )}
                      </span>
                    </Tooltip>
                  )}
                </div>
              }
            />
          ))}
        </React.Fragment>
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
}

export default MessageList;
